import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logo, logo_home, cart, user } from '../../Data/Images/Home';
import LogoSvg from '../../assets/logo.svg';
import Language from './Language';
import {
  selectSlug,
  setCookieBannerMessage,
  setTakeTheTest,
  setSlug,
} from '../../features/contentsSlice';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Login, HeaderIngredients, Shop, TakeTheTest } from '../../Constants';
import { getProfileActions } from '../../app/api/useDataManage/userActions';
import { useDispatch } from 'react-redux';
import { useGetContentsByTagWebQuery } from '../../features/contentsApiSlice';
import { selectCurrentLanguage } from '../../features/langSlice';
import { Icon } from '../../Components/elements/icon';
import DashboardMenu from '../../Components/Customer/components/DashboardMenu';
import CustomLink from '../../Components/CustomLink';
import useMeta from '../../hooks/useMeta';

const Header = ({ languageOptions }) => {
  const selectedLang = useSelector(selectCurrentLanguage);
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [scrolledPastBanner, setScrolledPastBanner] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const slug = useSelector(selectSlug);

  const [lang, setLang] = useState();
  const [skip] = useState(false);

  const [token] = useState(localStorage.getItem('customer_token'));
  const quiz_id = localStorage.getItem('quiz_id');
  const userData = useSelector((state) => state.user);

  const {
    data: fetchContents,
    isSuccess,
    refetch,
  } = useGetContentsByTagWebQuery({ tag: 'home', lang: lang, skip: skip });

  useEffect(() => {
    setLang(selectedLang === 'us' ? 'en' : selectedLang);

    refetch();
  }, [refetch, lang, selectedLang]);

  useEffect(() => {
    if (isSuccess) {
      if (fetchContents.data.length > 0) {
        dispatch(setCookieBannerMessage({ message: fetchContents.data[23].contents }));
        dispatch(setTakeTheTest({ title: fetchContents.data[24].title }));
        dispatch(setSlug({ slug: fetchContents.data[24].slug }));
      }
    }
  }, [fetchContents]);

  useEffect(() => {
    if (token && token !== '') {
      dispatch(getProfileActions(token));
    }
  }, [token, dispatch, userData.isAuthenticated]);

  const toggleMenu = (e) => {
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLoginDropdownOpen && !event.target.closest('.login-dropdown-container')) {
        setIsLoginDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLoginDropdownOpen]);

  useEffect(() => {
    if (!isHome) {
      setScrolledPastBanner(false);
      return;
    }
    const onScroll = () => setScrolledPastBanner(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const { metaEvent } = useMeta();

  return (
    <>
      <header className={`sticky-top t-transition-all t-duration-200 ${isMenuOpen ? ' sidebar-active' : ''}${isHome && scrolledPastBanner ? ' t-border-b t-border-white/20' : ''}`}>
        <nav className="navbar navbar-expand-xl navbar-light !t-bg-white !t-py-0 xl:!t-py-2 t-border-b t-border-navy/10">
          <div className="container">
            <div className="d-flex justify-content-between w-100 align-items-center d-xl-none t-bg-white t-relative !t-py-1 t-px-3 t-rounded-none t-min-h-[44px]">
              {isHome && scrolledPastBanner ? (
                <CustomLink to={'/'}>
                  <img src={LogoSvg} alt="logo riise" className="t-h-10 t-w-auto" />
                </CustomLink>
              ) : (
                <>
                  <div className="t-w-10" />
                  <CustomLink to={'/'} className="t-absolute t-left-1/2 t--translate-x-1/2">
                    <img src={LogoSvg} alt="logo riise" className="t-h-10 t-w-auto" />
                  </CustomLink>
                </>
              )}

              {isHome && scrolledPastBanner && (
                <CustomLink
                  to="/quiz"
                  onClick={() => metaEvent('Lead')}
                  className="t-absolute t-left-1/2 t--translate-x-1/2"
                >
                  <span className="btn btn-custom-tertiary t-font-sans !t-rounded-lg !t-bg-primary !t-text-secondary !t-text-sm !t-py-1 !t-px-4">
                    {t(TakeTheTest)}
                  </span>
                </CustomLink>
              )}

              <div className="d-flex t-items-center t-gap-3 t-ml-auto">
                <div className="mobile:!t-hidden t-hidden">
                  <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="sm:!t-hidden ">
                    <span className="btn btn-custom !t-rounded-lg t-font-sans">{t(TakeTheTest)}</span>
                  </CustomLink>
                </div>
                <div className="d-sm-flex d-none">
                  {quiz_id && !token && (
                    <div className="t-relative">
                      <CustomLink className="cart" to="/product-suggestion">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                      </CustomLink>
                      <div className="!t-h-3 !t-w-3 !t-rounded-full t-bg-red-500 t-absolute -t-right-1 t-top-0" />
                    </div>
                  )}

                  {userData.isAuthenticated === true && token !== '' ? (
                    <CustomLink className="login" to={'/dashboard'}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
                      <span>{t(userData?.user?.name)}</span>
                    </CustomLink>
                  ) : (
                    <div className="t-ml-4 t-relative login-dropdown-container">
                      <div
                        className="t-flex t-items-center t-gap-1 t-cursor-pointer"
                        onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
                        <span className="t-font-sans t-text-navy t-font-bold">{t(Login)}</span>
                      </div>
                      {isLoginDropdownOpen && (
                        <div className="t-absolute t-top-full t-right-1/2 t-mt-2 t-bg-white t-rounded-sm t-shadow-lg t-py-2 t-z-50 t-border t-border-gray-300">
                          <CustomLink
                            to="/login"
                            className="dropdown-item"
                            onClick={() => setIsLoginDropdownOpen(false)}
                          >
                            <span className="t-font-sans t-text-base t-text-secondary t-font-bold">{t('CustomerLogin')}</span>
                          </CustomLink>
                          <a
                            href="https://riise-partner.weriise.co/practitioner/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dropdown-item"
                          >
                            <span className="t-font-sans t-text-base t-text-secondary t-font-bold">
                              {t('PractitionerLogin')}
                            </span>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Icon
                  name="menu"
                  color="#103642"
                  onClick={(e) => toggleMenu(e)}
                  className="lg:t-hidden navbar-toggler border-0 ms-2"
                />
              </div>
            </div>
            <div
              className="collapse navbar-collapse d-xl-flex d-none !t-justify-between"
              id="collapsibleNavId"
            >
              <div className="t-flex t-items-center t-gap-2  col-xl-4 ">
                <CustomLink
                  to="/quiz"
                  className="btn btn-custom !t-rounded-lg t-font-sans"
                  onClick={() => metaEvent('Lead')}
                >
                  {t(TakeTheTest)}
                </CustomLink>
                <CustomLink
                  to="/ingredients"
                  onClick={() => metaEvent('ViewContent')}
                  className="btn !t-text-navy t-font-sans !t-pl-4 !t-pr-2"
                >
                  {t(HeaderIngredients)}
                </CustomLink>
                <CustomLink to="/products" className="btn !t-text-navy t-font-sans !t-px-2">
                  {t(Shop)}
                </CustomLink>
              </div>

              <ul className="navbar-nav mx-auto mt-2 mt-lg-0 d-xl-flex !t-items-center !t-justify-center d-none  col-xl-4 ">
                <li className="nav-item">
                  <CustomLink to={`/`}>
                    <img src={LogoSvg} alt="logo riise" className="img-fluid" width={80} />
                  </CustomLink>
                </li>
              </ul>
              <ul className="navbar-nav mt-2 mt-lg-0 d-xl-flex d-none align-items-center col-xl-4 justify-content-end ">
                {quiz_id && !token && (
                  <li className="!t-relative t-border-r-[1px] t-border-navy/20 t-pr-4">
                    <CustomLink className="cart" to="/product-suggestion">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                    </CustomLink>
                    <div className="!t-h-3 !t-w-3 !t-rounded-full t-bg-red-500 t-absolute t-right-3 t-top-0" />
                  </li>
                )}

                <li className="nav-item t-pl-6">
                  {userData.isAuthenticated === true && token !== '' ? (
                    <CustomLink className="t-flex t-items-center t-gap-1" to={'/dashboard'}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
                      <span className="t-font-sans t-text-navy t-font-bold">{t(userData?.user?.name)}</span>
                    </CustomLink>
                  ) : (
                    <div className="t-relative login-dropdown-container">
                      <div
                        className="t-flex t-items-center t-gap-1 t-cursor-pointer"
                        onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#103642" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
                        <span className="t-font-sans t-text-navy t-font-bold">{t(Login)}</span>
                      </div>
                      {isLoginDropdownOpen && (
                        <div className="t-absolute t-top-full t-right-1/2 t-mt-2 t-bg-white t-rounded-sm t-shadow-lg t-py-2 t-z-50 t-border t-border-gray-300">
                          <CustomLink
                            to="/login"
                            className="dropdown-item"
                            onClick={() => setIsLoginDropdownOpen(false)}
                          >
                            <span className="t-font-sans t-text-base t-text-secondary t-font-bold">{t('CustomerLogin')}</span>
                          </CustomLink>
                          <a
                            href="https://riise-partner.weriise.co/practitioner/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dropdown-item"
                          >
                            <span className="t-font-sans t-text-base t-text-secondary t-font-bold">
                              {t('PractitionerLogin')}
                            </span>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </li>
                <Language darkText options={languageOptions} />
              </ul>
            </div>
          </div>
        </nav>
        <div className="mobile-overlay" />
        <div className="mobile-sidebar t-flex t-flex-col t-py-8 md:t-py-16">
          <Icon
            name="cross"
            color="#fff"
            size="16"
            className="t-absolute t-top-2 t-right-2 t-flex t-items-center t-justify-center t-p-1 t-bg-secondary t-rounded-full"
            onClick={(e) => toggleMenu(e)}
          />
          <CustomLink to="/" className="t-px-4 t-block">
            <img src={logo_home} alt="" className="t-h-7 t-w-auto !t-mb-3" />
          </CustomLink>

          <CustomLink
            to="/quiz"
            onClick={() => { metaEvent('Lead'); setIsMenuOpen(false); }}
            className="t-mx-4 t-mb-4 t-rounded-xl t-overflow-hidden t-relative t-flex t-flex-col t-justify-end t-no-underline"
            style={{ minHeight: '170px' }}
          >
            <img
              src="/bannermenu.webp"
              alt=""
              className="t-absolute t-inset-0 t-w-full t-h-full t-object-cover"
            />
            <div className="t-absolute t-inset-0 t-bg-gradient-to-t t-from-navy/90 t-via-navy/40 t-to-transparent" />
            <div className="t-relative t-z-10 t-p-4">
              <p className="t-font-sans t-text-white/80 t-text-xs t-m-0 t-mb-2">
                {t('MenuQuizSubtext')}
              </p>
              <span className="t-inline-block t-bg-butter t-text-navy t-font-sans t-font-bold t-text-sm t-px-4 t-py-2 t-rounded-lg">
                {t(TakeTheTest)}
              </span>
            </div>
          </CustomLink>

          <DashboardMenu setIsMenuOpen={setIsMenuOpen} />
          <div className="t-relative t-shrink-0 t-px-4">
            <Language isSidebar options={languageOptions} />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
